import { useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { punchApi, type PunchList, type PunchSignoff, type PunchTrade } from '@/lib/apiClient';
import { useGraphToken, useThumbnailCache } from '@/lib/graphHooks';
import { Button } from '@/components';
import { isConflictError, statusOf } from '@/lib/httpErrors';
import {
  locationLabel, tradeName, shortDate, plural, beforePhotoUrl, finalAfterPhoto, reportSummary, videoCountLine, type ReportItem,
} from './punchModel';
import { usePrintPreview, readyToPrint } from './usePrintPreview';

/**
 * The close-out report (#518 phase 2, design "Download Close-out Report"): the
 * closed-out list as the record kept at the end of the job. Every item, by
 * number, with its before and after photos and its key sign-offs — who marked
 * it fixed, who verified it, the architect/owner approval — and how often it
 * was rejected. Not the full history: that stays on each item.
 *
 * Printed the way the sub sheets are: the browser's print dialog, from a
 * portal on <body>. The root carries `pl-sheets`, so the sheets' print rules
 * hide everything else and print only the report.
 */
export default function PunchCloseoutReport({ projectId, trades, ewCrewLabel, jobNumber, jobTitle, jobLoaded, jobError = false, onClose }: {
  projectId: number;
  trades: PunchTrade[];
  /** How the E&W crew is named, so its rejections group with its items. */
  ewCrewLabel: string;
  /** '' until the job's details load, and for a job with no number. */
  jobNumber: string;
  jobTitle: string;
  /** The job's details have loaded (with or without a number). */
  jobLoaded: boolean;
  /** The job's details couldn't be loaded. */
  jobError?: boolean;
  onClose: () => void;
}) {
  // Under the list's key, so every refresh of the list refreshes this too.
  // Not kept once closed (gcTime 0): opened again after a later close-out,
  // a cached copy would show — and print — the earlier record while the new
  // one loads (review).
  const q = useQuery({ queryKey: ['punch', projectId, 'report'], queryFn: () => punchApi.report(projectId), gcTime: 0 });
  const items = useMemo(() => q.data?.items ?? [], [q.data]);
  const summary = useMemo(() => reportSummary(items, ewCrewLabel), [items, ewCrewLabel]);

  const graph = useGraphToken();
  const photoUrls = useMemo(
    () => [...new Set(items.flatMap((i) => [beforePhotoUrl(i), finalAfterPhoto(i)?.webUrl]).filter((u): u is string => !!u))],
    [items],
  );
  const { thumbs, settled, reset } = useThumbnailCache(photoUrls, graph.silent);
  const loading = photoUrls.some((u) => !settled(u));

  const previewRef = useRef<HTMLDivElement>(null);
  const [preparing, setPreparing] = useState(false);
  // Never busy: closing while Print checks the report or waits on photos is
  // safe (`mounted` stops the print), and locking Back on a network wait
  // could trap the user in the report (review of #583).
  const { closeRef, mounted } = usePrintPreview(onClose, false);

  // Why the last Print didn't go ahead, when the report was no longer current.
  const [printNote, setPrintNote] = useState<string | null>(null);

  const print = async () => {
    setPreparing(true);
    setPrintNote(null);
    const shown = q.data;
    try {
      // Checked again on Print: the report can sit open while someone reopens
      // an item, and the old record mustn't print once the server no longer
      // stands by it (Codex on #583). Printed only if it is the same record —
      // React Query keeps an unchanged answer as the same object.
      const fresh = await q.refetch();
      if (!mounted.current) return;
      if (fresh.isError) {
        setPrintNote(isConflictError(fresh.error)
          ? 'The punch list was reopened or changed, so this is no longer its close-out record. Go back to the list.'
          : 'Couldn’t check that the report is still current. Try again.');
        return;
      }
      if (fresh.data !== shown) {
        setPrintNote('The report changed since it opened. Check it, then print again.');
        return;
      }
      if (!(await readyToPrint(previewRef.current, photoUrls, thumbs, graph.needsSignIn, mounted))) return;
      window.print();
    } finally {
      if (mounted.current) setPreparing(false);
    }
  };

  const list = q.data?.list;
  // The record has to say which job it is, so no printing until the job's
  // details are here (review). Waited for as a read, not as a number: a job
  // with none would wait forever, so it prints saying so (review).
  const jobKnown = jobLoaded;
  const jobLine = jobNumber ? `Job ${jobNumber}` : 'Job number not set';
  const printed = shortDate(new Date().toISOString(), { year: true });

  return createPortal(
    <div className="pl-sheets" role="dialog" aria-modal="true" aria-label="Close-out report">
      <div className="pl-sheets-bar">
        <button ref={closeRef} type="button" className="pl-link" onClick={onClose}>← Back to the list</button>
        <strong>Close-out report</strong>
        <span className="pl-spacer" />
        {graph.needsSignIn && photoUrls.length > 0 && (
          <Button variant="secondary" size="sm" onClick={async () => { try { await graph.interactive(); reset(); } catch { /* shown on next try */ } }}>
            Show photos
          </Button>
        )}
        {loading && !graph.needsSignIn && <span className="pl-muted">Loading photos…</span>}
        {printNote && <span className="pl-error" role="alert">{printNote}</span>}
        {!jobKnown && (jobError
          ? <span className="pl-error" role="alert">Couldn’t load the job’s details. Close the report and try again.</span>
          : <span className="pl-muted">Loading the job’s details…</span>)}
        <Button variant="primary" onClick={() => { void print(); }} isLoading={preparing}
          disabled={!list || !jobKnown || preparing || (loading && !graph.needsSignIn)}
          title={!jobKnown ? 'Waiting for the job’s details' : loading && !graph.needsSignIn ? 'Waiting for the photos to load' : undefined}>
          Print / Save as PDF
        </Button>
      </div>

      <div className="pl-sheets-preview" ref={previewRef}>
        {q.isPending && <p className="pl-muted">Loading the report…</p>}
        {q.isError && !list && (
          // "Changed while the report was being read" says try again: here,
          // not by closing and reopening the report (review of #583).
          <div className="pl-error pl-row" role="alert">
            <span>{loadError(q.error)}</span>
            <Button variant="secondary" size="sm" isLoading={q.isFetching} onClick={() => { void q.refetch(); }}>Try Again</Button>
          </div>
        )}
        {list && (
          <article className="pl-sheet pl-report"
            aria-label={`Close-out report for ${jobNumber ? `job ${jobNumber}` : jobTitle || 'this job'}`}>
            <header className="pl-sheet-hd">
              <div>
                <div className="pl-sheet-stage">Punch List Close-out Report</div>
                <h2>{jobTitle || jobLine}</h2>
                <div className="pl-sheet-meta">
                  {jobTitle && <>{jobLine}<br /></>}
                  Closed out {shortDate(list.closedOutAt, { year: true })}{list.closedOutBy ? ` by ${list.closedOutBy}` : ''} · {plural(summary.total, 'item')}
                </div>
              </div>
              <img src="/ew-logo-horizontal-green.png" alt="East & Westbrook" className="pl-sheet-logo" />
            </header>

            <RecordFacts list={list} approved={summary.approved} needingApproval={summary.needingApproval} />

            <div className="pl-report-counts">
              <Count label="Items" value={summary.total} />
              <Count label="Internal punch" value={summary.internal} />
              <Count label="Added at owner walk" value={summary.addedAtWalk} />
              {summary.notCarriedOver > 0 && <Count label="Not carried over" value={summary.notCarriedOver} />}
              <Count label="Rejected at least once" value={summary.rejectedOnce} />
            </div>

            <table className="pl-report-subs">
              <thead><tr><th>Assigned to</th><th>Items</th><th>Rejections</th></tr></thead>
              <tbody>
                {summary.subs.map((r) => (
                  <tr key={r.label}><td>{r.label}</td><td>{r.items}</td><td>{r.rejections || '—'}</td></tr>
                ))}
              </tbody>
            </table>

            <div className="pl-sheet-items">
              {items.map((i) => (
                <ReportRow key={i.id} item={i} trades={trades} thumbs={thumbs} approvalOn={list.requiresAoApproval} />
              ))}
            </div>

            <footer className="pl-sheet-ft">
              <span>East &amp; Westbrook Construction · Buckner, KY</span>
              <span>Printed {printed}</span>
            </footer>
          </article>
        )}
      </div>
    </div>,
    document.body,
  );
}

function RecordFacts({ list, approved, needingApproval }: { list: PunchList; approved: number; needingApproval: number }) {
  return (
    <dl className="pl-report-facts">
      {list.publishedAt && (
        <><dt>Published</dt><dd>{shortDate(list.publishedAt, { year: true })}{list.publishedBy ? ` by ${list.publishedBy}` : ''}</dd></>
      )}
      {(list.walkDate || list.walkAttendees) && (
        <><dt>Architect/owner walk</dt><dd>{[shortDate(list.walkDate, { year: true }), list.walkAttendees].filter(Boolean).join(' · ')}</dd></>
      )}
      {list.architectName && <><dt>Architect</dt><dd>{list.architectName}</dd></>}
      {list.ownerRep && <><dt>Owner’s rep</dt><dd>{list.ownerRep}</dd></>}
      <dt>Architect/Owner approval</dt>
      <dd>
        {!list.requiresAoApproval
          ? 'Not required on this job'
          // 0 of 0 isn't "every item": with none to approve, say so (review).
          : needingApproval === 0
            ? 'Required · no item needed it'
            : approved === needingApproval
              ? 'Required · recorded for every item'
              : `Required · recorded for ${approved} of ${plural(needingApproval, 'item')}`}
      </dd>
    </dl>
  );
}

function Count({ label, value }: { label: string; value: number }) {
  return <div><div className="pl-report-count">{value}</div><div className="pl-caption">{label}</div></div>;
}

function ReportRow({ item, trades, thumbs, approvalOn }: {
  item: ReportItem; trades: PunchTrade[]; thumbs: Record<string, string>; approvalOn: boolean;
}) {
  const s = item.signoffs;
  const loc = locationLabel(item);
  const before = beforePhotoUrl(item);
  const after = finalAfterPhoto(item)?.webUrl;
  return (
    <div className="pl-sheet-row pl-report-row">
      <Photo url={before} thumbs={thumbs} caption="Before" />
      <Photo url={after} thumbs={thumbs} caption="After" />
      <div>
        <div className="pl-sheet-where">{item.label}{loc !== '—' ? ` · ${loc}` : ''}</div>
        <div className="pl-sheet-desc">{item.description}</div>
        {item.notes && <div className="pl-sheet-notes">{item.notes}</div>}
        <div className="pl-sheet-sub">{tradeName(trades, item.trade)} · {item.assigneeLabel ?? 'Unassigned'}</div>
        {/* A printed page can't play a clip: it says the item has one (#518 phase 2). */}
        {videoCountLine(item) && <div className="pl-sheet-sub">{videoCountLine(item)}</div>}
        <ul className="pl-report-signoffs">
          {s.closed?.notCarriedOver ? (
            <li>Closed at publish{when(s.closed)} — not carried over to the published list</li>
          ) : (
            <>
              {s.fixed && <li>Marked fixed{when(s.fixed)}</li>}
              {s.verified && <li>Verified{when(s.verified)}</li>}
              {s.approved
                ? <li>Architect/Owner approval recorded{when(s.approved)}</li>
                : approvalOn && <li className="pl-muted">No architect/owner approval recorded</li>}
            </>
          )}
          {s.rejections > 0 && (
            // "Before it passed" only of work that passed: an item closed at
            // publish never did (review).
            <li>
              Rejected {s.rejections === 1 ? 'once' : `${s.rejections} times`}{s.closed?.notCarriedOver ? '' : ' before it passed'}
            </li>
          )}
        </ul>
      </div>
    </div>
  );
}

function Photo({ url, thumbs, caption }: { url: string | undefined; thumbs: Record<string, string>; caption: string }) {
  const src = url ? thumbs[url] : undefined;
  return (
    <figure className="pl-report-photo">
      {src ? <img className="pl-sheet-photo" src={src} alt={`${caption} photo`} /> : <span className="pl-sheet-photo" aria-hidden />}
      <figcaption>{url ? caption : `No ${caption.toLowerCase()} photo`}</figcaption>
    </figure>
  );
}

/**
 * Why the report didn't load. The server's reasons (a 409 says the list
 * changed) read as written; with no answer at all — the timeout, a dropped
 * connection — the client's own text ("timeout of 60000ms exceeded") would
 * show, so it is said plainly instead (review of #583).
 */
function loadError(e: unknown): string {
  if (statusOf(e) === undefined) return 'Couldn’t reach the Project Tracker to load the report. Check your connection and try again.';
  return (e as Error | null)?.message || 'Couldn’t load the report.';
}

/** " Oct 3, 2026 by Name" */
function when(s: PunchSignoff): string {
  return ` ${shortDate(s.at, { year: true })}${s.by ? ` by ${s.by}` : ''}`;
}

